// PR inference dashboard and aggregates, with their request caches.
import { jwtDecode } from 'jwt-decode';
import {
  ProductRecognitionInferenceAggregateResponse,
  ProductRecognitionInferenceDashboardResponse,
  ProductRecognitionInferencePlotAnnotation,
  ProductRecognitionInferencePlotAnnotationRequest,
  ProductRecognitionInferencePlotAnnotationsResponse,
} from '../../types/productRecognitionInference';
import { forgeRequest } from '../forgeRequest';
import { FORGE_DASHBOARDS_API_URL } from './endpoints';

const inFlightProductRecognitionInferenceRequests = new Map<
  string,
  Promise<ProductRecognitionInferenceDashboardResponse>
>();
// The inference caches make switching back and forth between dates, hours and
// chains instant. Unbounded they also retain every response of every session
// for the life of the tab, and keep serving a snapshot the hourly backend
// refresh has already superseded — the page has no refresh control, so a
// stale entry could never be displaced. Both caches are therefore bounded by
// entry count and by age, and are dropped wholesale when the session changes.
const PR_INFERENCE_CACHE_TTL_MS = 5 * 60 * 1000;
const PR_INFERENCE_CACHE_MAX_ENTRIES = 24;

/** Most-recently-used-last cache that forgets entries by age and by count. */
class BoundedTtlCache<T> {
  private readonly entries = new Map<string, { value: T; cachedAt: number }>();

  constructor(
    private readonly maxEntries: number,
    private readonly ttlMs: number
  ) {}

  get(key: string): T | undefined {
    const entry = this.entries.get(key);
    if (entry === undefined) return undefined;
    if (Date.now() - entry.cachedAt >= this.ttlMs) {
      this.entries.delete(key);
      return undefined;
    }
    // A Map iterates in insertion order, so re-inserting marks this key as the
    // most recently used and keeps eviction below honest.
    this.entries.delete(key);
    this.entries.set(key, entry);
    return entry.value;
  }

  set(key: string, value: T): void {
    this.entries.delete(key);
    this.entries.set(key, { value, cachedAt: Date.now() });
    while (this.entries.size > this.maxEntries) {
      const oldestKey = this.entries.keys().next();
      if (oldestKey.done) break;
      this.entries.delete(oldestKey.value);
    }
  }

  clear(): void {
    this.entries.clear();
  }
}

const productRecognitionInferenceDashboardCache =
  new BoundedTtlCache<ProductRecognitionInferenceDashboardResponse>(
    PR_INFERENCE_CACHE_MAX_ENTRIES,
    PR_INFERENCE_CACHE_TTL_MS
  );
const inFlightProductRecognitionInferenceAggregateRequests = new Map<
  string,
  Promise<ProductRecognitionInferenceAggregateResponse>
>();
const productRecognitionInferenceAggregateCache =
  new BoundedTtlCache<ProductRecognitionInferenceAggregateResponse>(
    PR_INFERENCE_CACHE_MAX_ENTRIES,
    PR_INFERENCE_CACHE_TTL_MS
  );
const inFlightProductRecognitionInferenceAnnotationRequests = new Map<
  string,
  Promise<ProductRecognitionInferencePlotAnnotationsResponse>
>();
let productRecognitionInferenceCacheGeneration = 0;
let productRecognitionInferenceCacheToken: string | null = null;

/**
 * Identity of the signed-in user, for deciding whether the cache still belongs
 * to them. The token string is not that identity: `AuthContext` extends the
 * session well before expiry and swaps in a fresh token for the same user, and
 * keying on the raw string would read every extension as a new sign-in and
 * throw away a cache that is still perfectly valid. Falls back to the token
 * when no claim can be read, which only costs an extra clear.
 */
function sessionIdentityOf(token: string): string {
  try {
    const claims = jwtDecode<{ id?: string; sub?: string; email?: string }>(token);
    return claims.id ?? claims.sub ?? claims.email ?? token;
  } catch {
    return token;
  }
}

/**
 * Drops cached inference responses when the signed-in user changes: the
 * previous user's data is unreachable by then, and keeping it retains a full
 * set of responses nobody can display. Bumping the generation also disowns the
 * requests already in flight for that user, so their responses cannot land in
 * the new user's cache.
 */
function noteProductRecognitionInferenceSession(token: string): void {
  const identity = sessionIdentityOf(token);
  if (productRecognitionInferenceCacheToken === identity) return;
  productRecognitionInferenceCacheToken = identity;
  productRecognitionInferenceCacheGeneration += 1;
  productRecognitionInferenceDashboardCache.clear();
  productRecognitionInferenceAggregateCache.clear();
  // Stop the new session from joining a request issued with the old token.
  // Callers already awaiting those promises still get their result.
  inFlightProductRecognitionInferenceRequests.clear();
  inFlightProductRecognitionInferenceAggregateRequests.clear();
  inFlightProductRecognitionInferenceAnnotationRequests.clear();
}

/**
 * Removes a settled request from its in-flight map, but only while that map
 * still holds this very request: a session change clears the map, and a later
 * request may already have claimed the key.
 */
function clearInFlightRequest<T>(
  requests: Map<string, Promise<T>>,
  key: string,
  request: Promise<T>
): void {
  if (requests.get(key) === request) {
    requests.delete(key);
  }
}

export async function fetchProductRecognitionInferenceDashboard(
  token: string,
  date: string,
  hour?: number | null,
  chainId?: number | null
): Promise<ProductRecognitionInferenceDashboardResponse> {
  noteProductRecognitionInferenceSession(token);
  // The session, not the key, separates one user's cached responses from the
  // next: a token change clears the cache outright.
  const requestKey = `${date}:${hour ?? 'combined'}:${chainId ?? 'all'}`;
  const cachedResponse = productRecognitionInferenceDashboardCache.get(requestKey);
  if (cachedResponse) {
    return cachedResponse;
  }
  const existingRequest = inFlightProductRecognitionInferenceRequests.get(requestKey);
  if (existingRequest) {
    return existingRequest;
  }

  const params = new URLSearchParams({ date });
  if (hour !== null && hour !== undefined) params.set('hour', String(hour));
  if (chainId) params.set('chain_id', String(chainId));
  const cacheGeneration = productRecognitionInferenceCacheGeneration;
  const request = (async (): Promise<ProductRecognitionInferenceDashboardResponse> => {
    const body = await forgeRequest<ProductRecognitionInferenceDashboardResponse>(
      token,
      `${FORGE_DASHBOARDS_API_URL}/product-recognition-inference?${params.toString()}`,
      'Error fetching Product Recognition inference dashboard'
    );
    if (cacheGeneration === productRecognitionInferenceCacheGeneration) {
      productRecognitionInferenceDashboardCache.set(requestKey, body);
    }
    return body;
  })();

  inFlightProductRecognitionInferenceRequests.set(requestKey, request);
  try {
    return await request;
  } finally {
    clearInFlightRequest(inFlightProductRecognitionInferenceRequests, requestKey, request);
  }
}

export async function fetchProductRecognitionInferenceAggregate(
  token: string,
  dateFrom: string,
  dateTo: string,
  hour?: number | null,
  chainId?: number | null,
  modPrefix?: string | null
): Promise<ProductRecognitionInferenceAggregateResponse> {
  noteProductRecognitionInferenceSession(token);
  const requestKey = `${dateFrom}:${dateTo}:${hour ?? 'combined'}:${chainId ?? 'all'}:${modPrefix ?? 'all'}`;
  const cachedResponse = productRecognitionInferenceAggregateCache.get(requestKey);
  if (cachedResponse) {
    return cachedResponse;
  }
  const existingRequest = inFlightProductRecognitionInferenceAggregateRequests.get(requestKey);
  if (existingRequest) {
    return existingRequest;
  }

  const params = new URLSearchParams({
    date_from: dateFrom,
    date_to: dateTo,
  });
  if (hour !== null && hour !== undefined) params.set('hour', String(hour));
  if (chainId) params.set('chain_id', String(chainId));
  if (modPrefix) params.set('mod_prefix', modPrefix);
  const cacheGeneration = productRecognitionInferenceCacheGeneration;
  const request = (async (): Promise<ProductRecognitionInferenceAggregateResponse> => {
    const body = await forgeRequest<ProductRecognitionInferenceAggregateResponse>(
      token,
      `${FORGE_DASHBOARDS_API_URL}/product-recognition-inference/aggregate?${params.toString()}`,
      'Error fetching Product Recognition inference aggregate'
    );
    if (cacheGeneration === productRecognitionInferenceCacheGeneration) {
      productRecognitionInferenceAggregateCache.set(requestKey, body);
    }
    return body;
  })();

  inFlightProductRecognitionInferenceAggregateRequests.set(requestKey, request);
  try {
    return await request;
  } finally {
    clearInFlightRequest(inFlightProductRecognitionInferenceAggregateRequests, requestKey, request);
  }
}

export async function fetchProductRecognitionInferencePlotAnnotations(
  token: string,
  dateFrom: string,
  dateTo: string,
  chainId?: number | null
): Promise<ProductRecognitionInferencePlotAnnotationsResponse> {
  const requestKey = `${token}:${dateFrom}:${dateTo}:${chainId ?? 'all'}`;
  const existingRequest = inFlightProductRecognitionInferenceAnnotationRequests.get(requestKey);
  if (existingRequest) {
    return existingRequest;
  }

  const params = new URLSearchParams({
    date_from: dateFrom,
    date_to: dateTo,
  });
  if (chainId) params.set('chain_id', String(chainId));
  const request = forgeRequest<ProductRecognitionInferencePlotAnnotationsResponse>(
    token,
    `${FORGE_DASHBOARDS_API_URL}/product-recognition-inference/annotations?${params.toString()}`,
    'Error fetching Product Recognition inference daily notes'
  );

  inFlightProductRecognitionInferenceAnnotationRequests.set(requestKey, request);
  try {
    return await request;
  } finally {
    clearInFlightRequest(
      inFlightProductRecognitionInferenceAnnotationRequests,
      requestKey,
      request
    );
  }
}

export async function saveProductRecognitionInferencePlotAnnotation(
  token: string,
  request: ProductRecognitionInferencePlotAnnotationRequest
): Promise<ProductRecognitionInferencePlotAnnotation> {
  return forgeRequest<ProductRecognitionInferencePlotAnnotation>(
    token,
    `${FORGE_DASHBOARDS_API_URL}/product-recognition-inference/annotations`,
    'Error saving Product Recognition inference daily note',
    {
      method: 'PUT',
      body: {
        selected_date: request.selectedDate,
        chain_id: request.chainId,
        note: request.note,
      },
    }
  );
}
